import React, { useState, useCallback } from 'react'
import { useRichTextEditorContext, MenuButton } from 'mui-tiptap'
import { MenuItem, Box, Menu, IconButton, type SxProps, type Theme } from '@mui/material'
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'

import lang from '@/lang'
import ThemeInterface from '@/theme-interface'
import { useTheme } from '@mui/styles'

// Shared styles for list type icons
const iconStyle: React.CSSProperties = {
  fontWeight: 'bold',
  fontSize: '14px',
  minWidth: '20px',
  display: 'block',
}

// List type icon components
const DecimalIcon = () => <span style={iconStyle}>1.</span>
const UpperAlphaIcon = () => <span style={iconStyle}>A.</span>
const LowerAlphaIcon = () => <span style={iconStyle}>a.</span>
const UpperRomanIcon = () => <span style={iconStyle}>IV.</span>
const LowerRomanIcon = () => <span style={iconStyle}>iv.</span>
const DecimalWithLeadingZeroIcon = () => <span style={iconStyle}>01.</span>

export interface OrderedListTypeOption {
  value: string
  label: string
  IconComponent: React.ComponentType
}

export const orderedListTypeOptions: OrderedListTypeOption[] = [
  { value: 'decimal', IconComponent: DecimalIcon, label: lang.htmlInput.extensions.orderedList.type.decimal },
  { value: 'decimal-leading-zero', IconComponent: DecimalWithLeadingZeroIcon, label: lang.htmlInput.extensions.orderedList.type.decimalWithLeadingZero },
  { value: 'upper-alpha', IconComponent: UpperAlphaIcon, label: lang.htmlInput.extensions.orderedList.type.upperAlpha },
  { value: 'lower-alpha', IconComponent: LowerAlphaIcon, label: lang.htmlInput.extensions.orderedList.type.lowerAlpha },
  { value: 'upper-roman', IconComponent: UpperRomanIcon, label: lang.htmlInput.extensions.orderedList.type.upperRoman },
  { value: 'lower-roman', IconComponent: LowerRomanIcon, label: lang.htmlInput.extensions.orderedList.type.lowerRoman },
] as const

// Styles
const containerSx: SxProps<Theme> = (theme: ThemeInterface) => ({
  display: 'flex',
  alignItems: 'center',
  borderRadius: '6px',
  '& .dropdown-button': {
    borderLeft: '1px solid transparent',
  },
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    '& .dropdown-button': {
      borderLeftColor: theme.borderColor,
    },
  },
})

const menuButtonSx: SxProps<Theme> = {
  borderRadius: '6px 0 0 6px',
  '&:hover': {
    backgroundColor: 'transparent',
  },
}

const dropdownButtonSx: SxProps<Theme> = {
  minWidth: 'auto',
  width: '20px',
  height: '32px',
  padding: '4px 2px',
  borderRadius: '0 6px 6px 0',
  '&:hover': {
    backgroundColor: 'transparent',
  },
}

const menuItemSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  minWidth: 160,
}

export interface MenuSelectOrderedListTypeProps {
  tooltipTitle?: string
  disabled?: boolean
}

const MenuSelectOrderedListType: React.FC<MenuSelectOrderedListTypeProps> = ({
  tooltipTitle = lang.htmlInput.extensions.orderedList.type.label,
  disabled = false,
}) => {
  const editor = useRichTextEditorContext()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const theme = useTheme()

  const isListActive = Boolean(editor?.isActive('orderedList'))
  const open = Boolean(anchorEl)
  const currentValue = (() => {
    if (!isListActive) return 'decimal'
    const attributes = editor?.getAttributes('orderedList')
    return attributes?.listStyleType || 'decimal'
  })()

  const handleToggleList = useCallback(() => {
    if (!editor) return
    editor.chain().focus().toggleOrderedList().run()
  }, [editor])

  const handleDropdownClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const handleListTypeSelect = useCallback((listStyleType: string) => {
    if (!editor) return

    if (isListActive) {
      editor.chain().focus().updateAttributes('orderedList', { listStyleType }).run()
    } else {
      editor.chain()
        .focus()
        .toggleOrderedList()
        .updateAttributes('orderedList', { listStyleType })
        .run()
    }
    handleClose()
  }, [editor, isListActive, handleClose])

  return (
    <Box sx={containerSx(theme)}>
      <MenuButton
        tooltipLabel={tooltipTitle}
        disabled={disabled}
        selected={isListActive}
        onClick={handleToggleList}
        sx={menuButtonSx}
      >
        <FormatListNumberedIcon fontSize="small" />
      </MenuButton>

      <IconButton
        size="small"
        onClick={handleDropdownClick}
        disabled={disabled}
        className="dropdown-button"
        sx={dropdownButtonSx}
      >
        <ArrowDropDownIcon fontSize="small" />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {orderedListTypeOptions.map(({ value, IconComponent, label }) => (
          <MenuItem
            key={value}
            onClick={() => handleListTypeSelect(value)}
            selected={currentValue === value}
            sx={menuItemSx}
          >
            <IconComponent />
            <span style={{ fontSize: '14px' }}>{label}</span>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  )
}

export default MenuSelectOrderedListType
